import React from 'react';
import {classify} from 'src/utils/classify';
import {Markdown} from 'src/components/agentic-ui/markdown';
import {
  ChatBubble,
  ChatContent,
  ChatBody,
  BodyMedium,
  CHAT_BUBBLE_ORIENTATION,
  ChatAnchor,
} from '@spaced-out/ui-design-system';
import type {
  ActionEvent,
  ChatRole,
  Part,
  SubChatWindowStatus,
  WidgetPart,
} from 'src/components/agentic-ui/types';
import {ThinkingWidget} from './widgets/thinking-widget';
import {WidgetRenderer} from './widgets';
import {MessageFeedback} from './message-feedback';
import css from './default-message-view.css';


export type FlattenedPart = Part & {
  messageId: string;
  messageRole: ChatRole;
  showAssistantAnchor: boolean;
  isLastPartInMessage: boolean;
  feedbackRating?: number;
  feedbackComment?: string;
  subChatWindowStatus?: SubChatWindowStatus;
};
export type DefaultMessagePartViewProps = {
  part: FlattenedPart;
  role: ChatRole;
  publishMessageAction?: (action: ActionEvent) => void;
  onWidgetResize?: () => void;
  /**
   * When true, renders the part within a chat bubble (for plain text parts).
   * Default: true
   */
  showChatBubble?: boolean;
  /**
   * Optional id used for the `data-message-part-id` attribute.
   * Defaults to `part.id` when present.
   */
  messagePartId?: string;
  showAssistantAnchor: boolean;
  onSubmitFeedback?: (
    messageId: string,
    rating: number,
    comment: string,
  ) => Promise<void>;
  showFeedback: boolean;
};

export function DefaultMessagePartView({
  part,
  role,
  publishMessageAction,
  onWidgetResize,
  showChatBubble = true,
  messagePartId,
  showAssistantAnchor,
  onSubmitFeedback,
  showFeedback,
}: DefaultMessagePartViewProps) {
  const key = messagePartId ?? part.id ?? '';

  // Defensive: a docked widget part must only render in the dock, never inline.
  if (
    part.type === 'widget' &&
    (part as WidgetPart).data.widget_visibility === 'docked'
  ) {
    return null;
  }

  const effectiveShowFeedback =
    showFeedback &&
    part.isLastPartInMessage &&
    role === 'assistant' &&
    onSubmitFeedback &&
    part.messageId;

  if (part.type === 'plain_text' && part.data !== '') {
    const subChatMarker =
      part.subChatWindowStatus === 'max' ||
      part.subChatWindowStatus === 'min' ? (
        <span
          className={css.subChatMarker}
          data-sub-chat-status={part.subChatWindowStatus}
          aria-hidden="true"
        />
      ) : null;

    const content =
      role === 'user' ? (
        <ChatBody withBgColor>
          {/* pre-wrap so multi-line user messages (e.g. questionnaire Q/A
              submissions) keep their line breaks and blank-line gaps. */}
          <BodyMedium className={css.userMessageText}>{part.data}</BodyMedium>
        </ChatBody>
      ) : (
        <ChatBody classNames={{wrapper: css.chatBodyWrapper}}>
          <Markdown>{part.data}</Markdown>
        </ChatBody>
      );

    return (
      <div data-message-part-id={key} className={css.messagePartItem}>
        {subChatMarker}
        {showChatBubble ? (
          <ChatBubble
            orientation={
              role === 'user'
                ? CHAT_BUBBLE_ORIENTATION.right
                : CHAT_BUBBLE_ORIENTATION.left
            }
          >
            {showAssistantAnchor && <ChatAnchor isAI={true} />}
            <ChatContent classNames={{wrapper: css.chatContentWrapper}}>
              {content}
            </ChatContent>
          </ChatBubble>
        ) : (
          content
        )}
        {effectiveShowFeedback && (
          <MessageFeedback
            messageId={part.messageId!}
            savedRating={part.feedbackRating}
            savedComment={part.feedbackComment}
            onSubmit={onSubmitFeedback}
          />
        )}
      </div>
    );
  }

  if (part.type === 'thinking') {
    return (
      <div
        data-message-part-id={key}
        className={classify(css.messagePartItem, css.widget)}
      >
        <ThinkingWidget text={part.data as string} />
      </div>
    );
  }

  if (part.type === 'widget') {
    return (
      <div
        data-message-part-id={key}
        className={classify(css.messagePartItem, css.widget)}
      >
        <WidgetRenderer
          part={part}
          publishMessageAction={publishMessageAction}
          onResize={onWidgetResize}
        />
      </div>
    );
  }

  // action / non-visual
  return null;
}
